import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Paper,
  Popper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
  makeStyles
} from '@material-ui/core'
import Dayjs from 'dayjs'
import React from 'react'

import {
  confirmCapexReceipt,
  fetchCapexSheet,
  saveCapexSheet,
  searchCatalogue
} from 'api/procurement'
import constants from 'app/constants'
import { eventAlert } from 'app/helpers'

import { capexOrderSheet, capexOrderSpec } from '../components/capexPo'
import { errorText } from '../components/errorText'
import { downloadOrderPdf, downloadXlsxSheets } from '../components/exportUtils'
import { defaultOrderMonth } from '../constants'

// Monthly CAPEX exercise (September 2026 onward — July/August ran on Google
// Sheets). HQ publishes a curated item list + a per-centre budget; the centre
// enters quantities ONLY for what it is missing (blank/0 = we have it),
// drafts freely, and submits against a HARD budget block. Submit locks the
// month for HQ review; a rejection reopens it with HQ's remarks. Once HQ
// marks the month ordered the centre confirms receipt line by line as the
// items arrive (capex lands in trickles) — mirrors Consumables (2026-10-15).
const inr = (n) => `₹${Number(n || 0).toLocaleString('en-IN')}`

const STATUS_LABEL = {
  draft: 'Draft',
  submitted: 'Submitted — with HQ for review',
  approved: 'Approved by HQ',
  rejected: 'HQ asked for changes — edit and resubmit',
  sent: 'Ordered by HQ',
  received: 'Received'
}

const RECEIPT_LABEL = {
  pending: 'Awaiting',
  received: 'Received',
  not_received: 'Not received',
  discrepancy: 'Discrepancy',
  closed: 'Closed by HQ',
  unavailable: 'Unavailable — not ordered'
}
const RECEIPT_STYLE = {
  pending: { color: '#8d6e00', background: '#fff8e1' },
  received: { color: '#2e7d32', background: '#e8f5e9' },
  not_received: { color: '#c62828', background: '#ffebee' },
  discrepancy: { color: '#c62828', background: '#ffebee' },
  closed: { color: '#616161', background: '#eeeeee' },
  unavailable: { color: '#8a3d00', background: '#fff1e0' }
}

const LOCKED = ['submitted', 'approved', 'sent', 'received']

const useStyles = makeStyles((theme) => ({
  topRow: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(2),
    flexWrap: 'wrap',
    marginBottom: theme.spacing(1.5)
  },
  spacer: { flex: 1 },
  meter: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(3),
    padding: theme.spacing(1, 2),
    borderRadius: 8,
    marginBottom: theme.spacing(1.5)
  },
  meterOk: { backgroundColor: '#e8f5e9' },
  meterOver: { backgroundColor: '#ffebee' },
  meterNone: { backgroundColor: '#fff8e1' },
  metric: { display: 'flex', flexDirection: 'column' },
  metricValue: { fontWeight: 700 },
  statusBanner: {
    padding: theme.spacing(1, 2),
    borderRadius: 8,
    marginBottom: theme.spacing(1.5),
    backgroundColor: '#e3f2fd'
  },
  statusRejected: { backgroundColor: '#ffebee' },
  statusOrdered: { backgroundColor: '#ede7f6' },
  statusIssues: { backgroundColor: '#fff8e1' },
  statusDone: { backgroundColor: '#e8f5e9' },
  receiptChip: { height: 20, fontSize: 11, fontWeight: 600 },
  unavailBox: {
    marginTop: theme.spacing(2),
    padding: theme.spacing(1, 1.5),
    background: '#fff8ef',
    border: '1px solid #f3d9b5',
    borderRadius: 6
  },
  fitTable: { tableLayout: 'fixed', width: '100%', background: '#fff' },
  headCell: {
    position: 'sticky',
    top: 0,
    zIndex: 3,
    background: '#fff',
    borderBottom: '2px solid #ddd'
  },
  wrap: { wordBreak: 'break-word', whiteSpace: 'normal' },
  itemName: { fontWeight: 600 },
  // Same 'view ↗' idiom as the catalogue and the afterschool list, so a centre
  // can see what an item actually is without leaving the form (2026-08-25).
  itemLink: { marginLeft: 8, fontSize: 12, whiteSpace: 'nowrap' },
  inlineMeta: { color: theme.palette.text.secondary, fontSize: 12 },
  muted: { color: theme.palette.text.secondary },
  requested: { background: '#fffde7' },
  addedChip: {
    height: 18,
    fontSize: 10,
    fontWeight: 600,
    marginLeft: 6,
    background: '#e3f2fd',
    color: '#1565c0'
  },
  addRow: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1.5),
    flexWrap: 'wrap',
    padding: theme.spacing(1.5, 2),
    borderTop: '1px solid #eee'
  },
  totalRow: { '& td': { fontWeight: 700, borderTop: '2px solid #ddd' } }
}))

// Add an item that is not on the month's list, picked from the main catalogue
// (user 2026-11-03: "like program material"). Only priced items can be picked
// so the budget meter stays right; unpriced ones say "ask HQ".
function AddItemSearch({ excludeIds, onPick }) {
  const [query, setQuery] = React.useState('')
  const [results, setResults] = React.useState([])
  const [open, setOpen] = React.useState(false)
  const anchorRef = React.useRef(null)

  const run = React.useCallback(async (text) => {
    try {
      const res = await searchCatalogue(text)
      setResults(res?.results || [])
    } catch (e) {
      setResults([])
    }
  }, [])

  const shown = results.filter((r) => !excludeIds.has(r.uuid))
  return (
    <>
      <TextField
        size="small"
        variant="outlined"
        inputRef={anchorRef}
        placeholder="Search the catalogue to add an item…"
        value={query}
        style={{ minWidth: 320, flex: 1, maxWidth: 520 }}
        onChange={(e) => {
          setQuery(e.target.value)
          setOpen(true)
          run(e.target.value)
        }}
        onFocus={() => {
          setOpen(true)
          run(query)
        }}
        onBlur={() => setTimeout(() => setOpen(false), 200)}
      />
      <Popper
        open={open && shown.length > 0}
        anchorEl={anchorRef.current}
        placement="bottom-start"
        style={{ zIndex: 1300 }}
      >
        <Paper
          elevation={4}
          style={{
            width: anchorRef.current ? anchorRef.current.offsetWidth : 320,
            maxHeight: 300,
            overflowY: 'auto'
          }}
        >
          {shown.map((it) => {
            const priced = Number(it.cost) > 0
            return (
              <MenuItem
                key={it.uuid}
                disabled={!priced}
                onMouseDown={() => {
                  if (!priced) return
                  onPick(it)
                  setQuery('')
                  setOpen(false)
                }}
                style={{ display: 'flex', gap: 8, whiteSpace: 'normal' }}
              >
                <span style={{ flex: 1 }}>
                  {it.name}
                  {it.reference_id ? (
                    <span style={{ color: '#888', fontSize: 12 }}>
                      {' '}
                      · {it.reference_id}
                    </span>
                  ) : null}
                </span>
                <span style={{ fontSize: 12, whiteSpace: 'nowrap' }}>
                  {priced ? inr(it.cost) : 'no price — ask HQ'}
                </span>
              </MenuItem>
            )
          })}
        </Paper>
      </Popper>
    </>
  )
}

const CapexPage = ({ centreId, centreName }) => {
  const classes = useStyles()
  const [month, setMonth] = React.useState(defaultOrderMonth())
  const [sheet, setSheet] = React.useState(null)
  const [loading, setLoading] = React.useState(false)
  const [saving, setSaving] = React.useState(false)
  // qty + remarks drafts keyed by catalogue_item uuid; '' = not requested.
  const [qty, setQty] = React.useState({})
  const [remarks, setRemarks] = React.useState({})
  const [qItem, setQItem] = React.useState('')
  const [qCat, setQCat] = React.useState('all')
  const [requestedOnly, setRequestedOnly] = React.useState(false)
  const [confirmOpen, setConfirmOpen] = React.useState(false)
  // Receipt dialog: null = closed. receiptLines = [{ id, name, qty,
  // receipt_status, receipt_note }] for every ordered line.
  const [receipt, setReceipt] = React.useState(null)
  const [receiptLines, setReceiptLines] = React.useState([])
  const [receiptSaving, setReceiptSaving] = React.useState(false)

  const load = React.useCallback(async () => {
    if (!centreId || !month) return
    setLoading(true)
    try {
      const res = await fetchCapexSheet(centreId, month)
      setSheet(res)
      const q = {}
      const r = {}
      ;(res.items || []).forEach((it) => {
        q[it.catalogue_item] = Number(it.qty) > 0 ? String(it.qty) : ''
        r[it.catalogue_item] = it.centre_remarks || ''
      })
      setQty(q)
      setRemarks(r)
    } catch (error) {
      eventAlert(
        errorText(error, 'Failed to load the CAPEX sheet'),
        constants.ERROR,
        'Capex'
      )
    } finally {
      setLoading(false)
    }
  }, [centreId, month])

  React.useEffect(() => {
    load()
  }, [load])

  const items = sheet?.items || []
  const categories = [
    ...new Set(items.map((it) => it.category).filter(Boolean))
  ]
  const status = sheet?.status || 'draft'
  const locked = LOCKED.includes(status)
  // 'sent' = HQ has placed the order; 'received' = every line confirmed.
  const ordered = status === 'sent'
  const received = status === 'received'
  const receiptOn = ordered || received
  // HQ reopened the month after ordering (user 2026-11-03): lines that
  // already arrived (or were flagged / closed) keep their receipt state.
  const KEPT_RECEIPT = ['received', 'not_received', 'discrepancy', 'closed']
  const reopenedAfterOrder =
    status === 'draft' &&
    items.some(
      (it) => Number(it.qty) > 0 && KEPT_RECEIPT.includes(it.receipt_status)
    )
  const showReceiptChip = (it) =>
    receiptOn ||
    (reopenedAfterOrder && KEPT_RECEIPT.includes(it.receipt_status))
  const budget = sheet?.budget

  const lineTotal = (it) => {
    const n = Number(qty[it.catalogue_item])
    if (!n || Number.isNaN(n)) return 0
    return n * Number(it.unit_price || it.cost || 0)
  }
  // HQ marked these unavailable when ordering (user 2026-10-22): never
  // bought, so they don't count in the month's spend.
  // Once reopened the centre may keep the line; HQ decides it again.
  const isUnavailable = (it) => receiptOn && it.receipt_status === 'unavailable'
  const unavailableItems = items.filter(
    (it) => isUnavailable(it) && Number(qty[it.catalogue_item]) > 0
  )
  const unavailableTotal = unavailableItems.reduce(
    (s, it) => s + lineTotal(it),
    0
  )
  const requestedTotal = items.reduce(
    (s, it) => s + (isUnavailable(it) ? 0 : lineTotal(it)),
    0
  )
  const left = budget != null ? Number(budget) - requestedTotal : null
  const over = left != null && left < 0
  const nRequested = items.filter(
    (it) => Number(qty[it.catalogue_item]) > 0
  ).length

  const q = qItem.trim().toLowerCase()
  const shown = items.filter(
    (it) =>
      (!q ||
        it.name.toLowerCase().includes(q) ||
        (it.reference_id || '').toLowerCase().includes(q)) &&
      (qCat === 'all' || it.category === qCat) &&
      (!requestedOnly || Number(qty[it.catalogue_item]) > 0)
  )

  const monthLabel = Dayjs(`${month}-01`).format('MMMM YYYY')

  // Centre-added catalogue item: lives on the sheet locally until saved.
  const addExtra = (ci) => {
    setSheet((prev) => ({
      ...prev,
      items: [
        ...(prev?.items || []),
        {
          catalogue_item: ci.uuid,
          reference_id: ci.reference_id || '',
          name: ci.name,
          unit: ci.unit,
          cost: ci.cost,
          unit_price: ci.cost,
          reference_link: ci.reference_link || '',
          category: ci.category,
          qty: 0,
          receipt_status: 'pending',
          extra: true
        }
      ]
    }))
    setQty((prev) => ({ ...prev, [ci.uuid]: '1' }))
    setQItem('')
    setRequestedOnly(false)
  }
  const removeExtra = (id) => {
    setSheet((prev) => ({
      ...prev,
      items: (prev?.items || []).filter((it) => it.catalogue_item !== id)
    }))
    setQty((prev) => ({ ...prev, [id]: '' }))
  }
  const nAdded = items.filter(
    (it) => it.extra && Number(qty[it.catalogue_item]) > 0
  ).length

  const save = async (submit) => {
    setSaving(true)
    try {
      const lines = items
        .filter((it) => Number(qty[it.catalogue_item]) > 0)
        .map((it) => ({
          catalogue_item: it.catalogue_item,
          qty: Number(qty[it.catalogue_item]),
          centre_remarks: remarks[it.catalogue_item] || ''
        }))
      const res = await saveCapexSheet({
        centre: centreId,
        month: `${month}-01`,
        status: submit ? 'submitted' : 'draft',
        lines
      })
      eventAlert(
        submit
          ? `Submitted for HQ review — PO ${res.po_number}`
          : `Draft saved — ${res.lines} item${res.lines === 1 ? '' : 's'}`,
        constants.SUCCESS,
        'Capex'
      )
      setConfirmOpen(false)
      load()
    } catch (error) {
      eventAlert(errorText(error, 'Save failed'), constants.ERROR, 'Capex')
    } finally {
      setSaving(false)
    }
  }

  // Open the per-line receipt dialog. Lines still awaiting default to
  // "Received" so the common case is one click; the centre flags anything
  // not received or wrong. Already-confirmed lines keep their state and can
  // be corrected.
  const openReceipt = () => {
    setReceiptLines(
      items
        .filter(
          (it) =>
            Number(it.qty) > 0 &&
            it.receipt_status !== 'closed' &&
            !isUnavailable(it)
        )
        .map((it) => ({
          id: it.order || it.catalogue_item,
          name: it.name,
          qty: Number(it.qty),
          receipt_status:
            it.receipt_status && it.receipt_status !== 'pending'
              ? it.receipt_status
              : 'received',
          receipt_note: it.receipt_note || ''
        }))
    )
    setReceipt(true)
  }

  const updateReceiptLine = (i, patch) =>
    setReceiptLines((prev) =>
      prev.map((l, j) => (j === i ? { ...l, ...patch } : l))
    )

  // Partial receipt is normal for capex: whatever is set to Received /
  // Not received / Discrepancy is saved; lines left as "Still to come" stay
  // pending and the month stays Ordered until every line is confirmed.
  const submitReceipt = async () => {
    const missingNote = receiptLines.filter(
      (l) => l.receipt_status === 'discrepancy' && !l.receipt_note.trim()
    )
    if (missingNote.length) {
      eventAlert(
        'Add a note for each item marked as a discrepancy',
        constants.WARNING,
        'Capex'
      )
      return
    }
    setReceiptSaving(true)
    try {
      const res = await confirmCapexReceipt(
        centreId,
        `${month}-01`,
        receiptLines.map((l) => ({
          id: l.id,
          receipt_status: l.receipt_status,
          receipt_note: l.receipt_note.trim()
        }))
      )
      const done = res.status === 'received'
      eventAlert(
        done
          ? res.discrepancy
            ? 'Receipt confirmed with issues — HQ has been notified'
            : 'Receipt confirmed — all items received'
          : `${res.received_lines} of ${res.lines} items confirmed — ${res.pending_lines} still to come`,
        constants.SUCCESS,
        'Capex'
      )
      setReceipt(null)
      setReceiptLines([])
      load()
    } catch (error) {
      eventAlert(
        errorText(error, 'Failed to confirm receipt'),
        constants.ERROR,
        'Capex'
      )
    } finally {
      setReceiptSaving(false)
    }
  }

  // Excel/PDF of the request — the SAME purchase-order document HQ downloads
  // from the board (shared capexPo builder), so a centre and HQ never compare
  // two differently-shaped files. The catalogue link rides along as a real
  // hyperlink in both.
  const poLines = items
    .filter((it) => Number(qty[it.catalogue_item]) > 0)
    .map((it) => ({
      reference_id: it.reference_id,
      name: it.name,
      category: it.category,
      reference_link: it.reference_link,
      qty: Number(qty[it.catalogue_item]),
      unit_price: Number(it.unit_price || it.cost || 0),
      line_total: lineTotal(it),
      centre_remarks: remarks[it.catalogue_item] || ''
    }))
  const poOpts = {
    lines: poLines,
    centreName: centreName || '',
    monthLabel,
    po: sheet?.po_number || '',
    budget: budget != null ? budget : null,
    // Before submit this is a request, not yet an order — the title says so.
    title: locked ? 'Monthly Capex order' : 'Monthly Capex request',
    extraMeta: [
      { k: 'Status', v: STATUS_LABEL[status] || status },
      { k: 'Items', v: String(poLines.length) }
    ]
  }
  const fname = `Capex-${(centreName || 'centre').replace(
    /\s+/g,
    '-'
  )}-${month}`
  const onExcel = () =>
    downloadXlsxSheets(fname, [
      capexOrderSheet({ ...poOpts, sheetName: centreName || 'Capex' })
    ])
  const onPdf = () => downloadOrderPdf(fname, capexOrderSpec(poOpts))

  return (
    <div>
      <div className={classes.topRow}>
        <TextField
          type="month"
          size="small"
          variant="outlined"
          label="Month"
          value={month}
          onChange={(e) => setMonth(e.target.value)}
          InputLabelProps={{ shrink: true }}
        />
        <Chip
          size="small"
          label={STATUS_LABEL[status] || status}
          color={status === 'draft' ? 'default' : 'primary'}
        />
        {sheet?.po_number ? (
          <Typography variant="body2" className={classes.muted}>
            PO {sheet.po_number}
          </Typography>
        ) : null}
        <span className={classes.spacer} />
        {receiptOn && nRequested > 0 && (
          <Button
            size="small"
            variant="contained"
            color="primary"
            onClick={openReceipt}
          >
            {received ? 'Edit receipt' : 'Check & confirm receipt'}
          </Button>
        )}
        {nRequested > 0 && (
          <>
            <Button size="small" variant="outlined" onClick={onExcel}>
              Excel
            </Button>
            <Button size="small" variant="outlined" onClick={onPdf}>
              PDF
            </Button>
          </>
        )}
      </div>

      {status === 'rejected' && sheet?.review_remarks ? (
        <Paper
          className={`${classes.statusBanner} ${classes.statusRejected}`}
          elevation={0}
        >
          <Typography variant="body2">
            <b>HQ sent this back:</b> {sheet.review_remarks}
          </Typography>
        </Paper>
      ) : null}
      {reopenedAfterOrder ? (
        <Paper className={classes.statusBanner} elevation={0}>
          <Typography variant="body2">
            <b>HQ reopened the {monthLabel} order.</b> Edit and submit again —
            items already received stay marked received.
            {sheet?.review_remarks ? ` HQ: ${sheet.review_remarks}` : ''}
          </Typography>
        </Paper>
      ) : null}
      {ordered ? (
        <Paper
          className={`${classes.statusBanner} ${
            sheet?.discrepancy ? classes.statusIssues : classes.statusOrdered
          }`}
          elevation={0}
        >
          <Typography variant="body2">
            <b>HQ has ordered</b> the {monthLabel} items
            {sheet?.ordered_at
              ? ` on ${Dayjs(sheet.ordered_at).format('D MMM')}`
              : ''}
            . They may arrive in parts — as each one lands, click{' '}
            <b>Check &amp; confirm receipt</b> and mark it received, not
            received, or received with a discrepancy.
            {Number(sheet?.received_lines) > 0
              ? ` ${sheet.received_lines} of ${sheet.lines} confirmed so far, ${sheet.pending_lines} still to come.`
              : ''}
            {unavailableItems.length ? (
              <>
                {' '}
                <b style={{ color: '#8a3d00' }}>
                  {unavailableItems.length} item
                  {unavailableItems.length === 1 ? ' was' : 's were'}{' '}
                  unavailable
                </b>{' '}
                and won’t come — {inr(Math.round(unavailableTotal))} is not
                counted in your spend.
              </>
            ) : null}
            {sheet?.discrepancy && sheet?.discrepancy_notes
              ? ` Issues flagged to HQ: ${sheet.discrepancy_notes}`
              : ''}
          </Typography>
        </Paper>
      ) : received ? (
        <Paper
          className={`${classes.statusBanner} ${
            sheet?.discrepancy ? classes.statusIssues : classes.statusDone
          }`}
          elevation={0}
        >
          <Typography variant="body2">
            {sheet?.discrepancy ? (
              <>
                <b>Receipt confirmed with issues.</b>{' '}
                {sheet?.discrepancy_notes || ''} HQ has been notified.
              </>
            ) : (
              <>
                <b>Receipt confirmed</b> — all {monthLabel} items received.
                Thank you.
              </>
            )}
          </Typography>
        </Paper>
      ) : locked ? (
        <Paper className={classes.statusBanner} elevation={0}>
          <Typography variant="body2">
            {STATUS_LABEL[status]} — the {monthLabel} request is locked.
            {status === 'submitted'
              ? ' HQ can reopen it if something needs to change.'
              : ''}
          </Typography>
        </Paper>
      ) : null}

      <Box
        className={`${classes.meter} ${
          budget == null
            ? classes.meterNone
            : over
            ? classes.meterOver
            : classes.meterOk
        }`}
      >
        {budget == null ? (
          <Typography variant="body2">
            HQ has not set your {monthLabel} budget yet — you can save a draft
            but not submit.
          </Typography>
        ) : (
          <>
            <div className={classes.metric}>
              <Typography variant="caption" color="textSecondary">
                Budget
              </Typography>
              <Typography className={classes.metricValue}>
                {inr(budget)}
              </Typography>
            </div>
            <div className={classes.metric}>
              <Typography variant="caption" color="textSecondary">
                {unavailableItems.length ? 'Ordered' : 'Requested'} (
                {nRequested - unavailableItems.length} item
                {nRequested - unavailableItems.length === 1 ? '' : 's'})
              </Typography>
              <Typography className={classes.metricValue}>
                {inr(Math.round(requestedTotal))}
              </Typography>
            </div>
            {unavailableItems.length ? (
              <div className={classes.metric}>
                <Typography variant="caption" style={{ color: '#8a3d00' }}>
                  Unavailable (not ordered)
                </Typography>
                <Typography
                  className={classes.metricValue}
                  style={{ color: '#8a3d00' }}
                >
                  {inr(Math.round(unavailableTotal))}
                </Typography>
              </div>
            ) : null}
            <div className={classes.metric}>
              <Typography variant="caption" color="textSecondary">
                Left
              </Typography>
              <Typography
                className={classes.metricValue}
                style={{ color: over ? '#c62828' : '#2e7d32' }}
              >
                {inr(Math.round(left))}
              </Typography>
            </div>
            {over && (
              <Typography variant="body2" style={{ color: '#c62828' }}>
                Over budget — trim the list to submit.
              </Typography>
            )}
          </>
        )}
      </Box>

      {loading ? (
        <Box p={4} display="flex" justifyContent="center">
          <CircularProgress size={28} />
        </Box>
      ) : !items.length ? (
        <Typography variant="body2" color="textSecondary">
          HQ has not published the {monthLabel} CAPEX list yet. It appears here
          the moment they do.
        </Typography>
      ) : (
        <>
          <Typography variant="body2" color="textSecondary" gutterBottom>
            Enter a quantity only for what the centre is missing — blank means
            “we have it”.
          </Typography>
          <Paper variant="outlined">
            <Table size="small" className={classes.fitTable}>
              <TableHead>
                <TableRow>
                  <TableCell width="30%" className={classes.headCell}>
                    <TextField
                      size="small"
                      variant="outlined"
                      placeholder="Item / reference — search"
                      value={qItem}
                      onChange={(e) => setQItem(e.target.value)}
                      fullWidth
                    />
                  </TableCell>
                  <TableCell width="13%" className={classes.headCell}>
                    <TextField
                      select
                      size="small"
                      variant="outlined"
                      value={qCat}
                      onChange={(e) => setQCat(e.target.value)}
                      fullWidth
                    >
                      <MenuItem value="all">Category — all</MenuItem>
                      {categories.map((c) => (
                        <MenuItem key={c} value={c}>
                          {c}
                        </MenuItem>
                      ))}
                    </TextField>
                  </TableCell>
                  <TableCell width="9%" className={classes.headCell}>
                    Cost/pc
                  </TableCell>
                  <TableCell width="10%" className={classes.headCell}>
                    Qty needed
                  </TableCell>
                  <TableCell width="10%" className={classes.headCell}>
                    Total
                  </TableCell>
                  <TableCell width="28%" className={classes.headCell}>
                    <Box display="flex" alignItems="center" style={{ gap: 8 }}>
                      <Chip
                        size="small"
                        clickable
                        label={`requested only · ${nRequested}`}
                        color={requestedOnly ? 'primary' : 'default'}
                        onClick={() => setRequestedOnly((v) => !v)}
                      />
                      {!locked && (
                        <>
                          <Button
                            size="small"
                            variant="outlined"
                            color="primary"
                            disabled={saving}
                            onClick={() => save(false)}
                          >
                            Save draft
                          </Button>
                          <Button
                            size="small"
                            variant="contained"
                            color="primary"
                            disabled={
                              saving || over || budget == null || !nRequested
                            }
                            onClick={() => setConfirmOpen(true)}
                          >
                            Submit
                          </Button>
                        </>
                      )}
                    </Box>
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {shown.map((it) => {
                  const isRequested = Number(qty[it.catalogue_item]) > 0
                  return (
                    <TableRow
                      key={it.catalogue_item}
                      hover
                      className={isRequested ? classes.requested : undefined}
                    >
                      <TableCell className={classes.wrap}>
                        <span className={classes.itemName}>{it.name}</span>{' '}
                        <span className={classes.inlineMeta}>
                          {it.reference_id || ''}
                        </span>
                        {it.extra ? (
                          <Chip
                            size="small"
                            label="added by centre"
                            className={classes.addedChip}
                            title="Not on this month's list — added from the catalogue"
                          />
                        ) : null}
                        {it.extra && !locked ? (
                          <Button
                            size="small"
                            onClick={() => removeExtra(it.catalogue_item)}
                            style={{
                              minWidth: 0,
                              padding: '0 6px',
                              marginLeft: 4,
                              fontSize: 11,
                              color: '#c62828'
                            }}
                          >
                            remove
                          </Button>
                        ) : null}
                        {it.reference_link ? (
                          <a
                            href={it.reference_link}
                            target="_blank"
                            rel="noreferrer"
                            title="Open this item's product page"
                            className={classes.itemLink}
                          >
                            view ↗
                          </a>
                        ) : null}
                      </TableCell>
                      <TableCell className={`${classes.muted} ${classes.wrap}`}>
                        {it.category || '—'}
                      </TableCell>
                      <TableCell>{inr(it.unit_price || it.cost)}</TableCell>
                      <TableCell>
                        <TextField
                          size="small"
                          type="number"
                          variant="outlined"
                          disabled={locked}
                          value={qty[it.catalogue_item] || ''}
                          onChange={(e) => {
                            const v = e.target.value
                            setQty((prev) => ({
                              ...prev,
                              [it.catalogue_item]: v
                            }))
                          }}
                          inputProps={{ min: 0, style: { width: 52 } }}
                        />
                      </TableCell>
                      <TableCell>
                        {isRequested ? (
                          <span
                            style={
                              isUnavailable(it)
                                ? {
                                    textDecoration: 'line-through',
                                    color: '#757575'
                                  }
                                : undefined
                            }
                          >
                            {inr(Math.round(lineTotal(it)))}
                          </span>
                        ) : (
                          '—'
                        )}
                        {showReceiptChip(it) && isRequested ? (
                          <Chip
                            size="small"
                            className={classes.receiptChip}
                            style={{
                              marginLeft: 6,
                              ...RECEIPT_STYLE[it.receipt_status || 'pending']
                            }}
                            label={
                              RECEIPT_LABEL[it.receipt_status || 'pending']
                            }
                            title={
                              [it.receipt_note, it.procurement_remarks]
                                .filter(Boolean)
                                .join('\n') || ''
                            }
                          />
                        ) : null}
                        {receiptOn &&
                        isRequested &&
                        isUnavailable(it) &&
                        it.receipt_note ? (
                          <div className={classes.inlineMeta}>
                            HQ: {it.receipt_note}
                          </div>
                        ) : null}
                        {receiptOn && isRequested && it.procurement_remarks ? (
                          <div
                            className={classes.inlineMeta}
                            style={{ whiteSpace: 'pre-line' }}
                          >
                            {it.procurement_remarks}
                          </div>
                        ) : null}
                      </TableCell>
                      <TableCell>
                        <TextField
                          size="small"
                          variant="outlined"
                          placeholder="remark (optional)"
                          disabled={locked}
                          value={remarks[it.catalogue_item] || ''}
                          onChange={(e) => {
                            const v = e.target.value
                            setRemarks((prev) => ({
                              ...prev,
                              [it.catalogue_item]: v
                            }))
                          }}
                          fullWidth
                        />
                      </TableCell>
                    </TableRow>
                  )
                })}
                {!shown.length && (
                  <TableRow>
                    <TableCell colSpan={6}>
                      <Typography variant="body2" color="textSecondary">
                        No items match the search.
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
                <TableRow className={classes.totalRow}>
                  <TableCell>Total</TableCell>
                  <TableCell />
                  <TableCell />
                  <TableCell />
                  <TableCell>{inr(Math.round(requestedTotal))}</TableCell>
                  <TableCell />
                </TableRow>
              </TableBody>
            </Table>
            {!locked && (
              <Box className={classes.addRow}>
                <Typography variant="body2" style={{ fontWeight: 600 }}>
                  Need something not on the list?
                </Typography>
                <AddItemSearch
                  excludeIds={new Set(items.map((it) => it.catalogue_item))}
                  onPick={addExtra}
                />
                <Typography variant="caption" color="textSecondary">
                  Added items count against your budget and go to HQ with the
                  rest of the list.
                </Typography>
              </Box>
            )}
          </Paper>
        </>
      )}

      <Dialog
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        maxWidth="xs"
      >
        <DialogTitle>Submit {monthLabel} to HQ?</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            {nRequested} item{nRequested === 1 ? '' : 's'}
            {nAdded ? ` (${nAdded} added from the catalogue)` : ''} for{' '}
            {inr(Math.round(requestedTotal))} against a budget of {inr(budget)}.
            Submitting locks the month — only HQ can reopen it.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button size="small" onClick={() => setConfirmOpen(false)}>
            Keep editing
          </Button>
          <Button
            size="small"
            variant="contained"
            color="primary"
            disabled={saving}
            onClick={() => save(true)}
          >
            Submit
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={!!receipt}
        onClose={() => setReceipt(null)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Check &amp; confirm receipt — {monthLabel}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="textSecondary" gutterBottom>
            Capex items often arrive in parts. Mark what has landed{' '}
            <b>Received</b>, leave the rest as <b>Still to come</b> and come
            back when it arrives. Use <b>Not received</b> for an item that will
            not come, and <b>Received with discrepancy</b> (short, damaged,
            wrong item…) with a note. Not-received and discrepancy items are
            flagged to HQ per line.
          </Typography>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Item</TableCell>
                <TableCell style={{ width: 60 }}>Qty</TableCell>
                <TableCell style={{ width: 200 }}>Receipt</TableCell>
                <TableCell>Note (if discrepancy)</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {receiptLines.map((l, i) => (
                <TableRow key={l.id || i}>
                  <TableCell className={classes.wrap}>{l.name}</TableCell>
                  <TableCell>{l.qty}</TableCell>
                  <TableCell>
                    <TextField
                      select
                      size="small"
                      variant="outlined"
                      fullWidth
                      value={l.receipt_status}
                      onChange={(e) =>
                        updateReceiptLine(i, { receipt_status: e.target.value })
                      }
                    >
                      <MenuItem value="received">Received</MenuItem>
                      <MenuItem value="pending">Still to come</MenuItem>
                      <MenuItem value="not_received">Not received</MenuItem>
                      <MenuItem value="discrepancy">
                        Received with discrepancy
                      </MenuItem>
                    </TextField>
                  </TableCell>
                  <TableCell>
                    <TextField
                      size="small"
                      variant="outlined"
                      fullWidth
                      placeholder="What's wrong?"
                      disabled={l.receipt_status !== 'discrepancy'}
                      value={l.receipt_note}
                      onChange={(e) =>
                        updateReceiptLine(i, { receipt_note: e.target.value })
                      }
                    />
                  </TableCell>
                </TableRow>
              ))}
              {!receiptLines.length && (
                <TableRow>
                  <TableCell colSpan={4}>
                    <Typography variant="body2" color="textSecondary">
                      No ordered items to confirm.
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
          {unavailableItems.length ? (
            <Box className={classes.unavailBox}>
              <Typography
                variant="body2"
                style={{ fontWeight: 600, color: '#8a3d00' }}
                gutterBottom
              >
                Not ordered — HQ marked these unavailable. Nothing to confirm.
              </Typography>
              {unavailableItems.map((it) => (
                <Typography
                  key={it.catalogue_item}
                  variant="body2"
                  color="textSecondary"
                >
                  {it.name} × {Number(qty[it.catalogue_item])}
                  {it.receipt_note ? ` — HQ: ${it.receipt_note}` : ''}
                </Typography>
              ))}
            </Box>
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button size="small" onClick={() => setReceipt(null)}>
            Cancel
          </Button>
          <Button
            size="small"
            variant="contained"
            color="primary"
            disabled={receiptSaving || !receiptLines.length}
            onClick={submitReceipt}
          >
            {receiptSaving ? 'Saving…' : 'Save receipt'}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  )
}

export default CapexPage
